/**
 * TypeScript types matching shared/schemas/sprint-status.v1.json.
 *
 * In a follow-up we'll auto-generate these from the JSON Schema via
 * json-schema-to-typescript so backend Pydantic models and frontend types
 * stay locked together. For now they're hand-maintained.
 */

export const SCHEMA_VERSION = "1.0.0" as const

export type SchemaVersion = typeof SCHEMA_VERSION

export type PriorityMajor = "P0" | "P1" | "P2" | "P3"
export type PriorityMinor = "A" | "B" | null

export type StateCategory =
  | "pre_dev"
  | "in_dev"
  | "dev_complete"
  | "in_qa"
  | "done"
  | "removed"
  | "other"

export type WorkItemType =
  | "Bug"
  | "Project Item"
  | "Product Backlog Item"
  | "Task"
  | "Design Work Item"

export interface Sprint {
  number: number | null
  name: string
  iteration_path: string
  start: string | null
  end: string | null
}

export interface ParsedPriority {
  raw: string
  major: PriorityMajor
  minor: PriorityMinor
  is_prod: boolean
  is_uncertain: boolean
}

export interface AssignedTo {
  name: string
  email: string
  id: string
}

export interface SprintInfo {
  iteration_path: string
  number: number | null
  is_current: boolean
  is_previous: boolean
  is_next: boolean
  is_future: boolean
  is_past: boolean
}

export interface BundleInfo {
  id?: number
  url?: string
  title?: string
  code?: string | null
  state?: string
}

export interface WorkItem {
  id: number
  url: string
  title: string
  work_item_type: WorkItemType
  priority: ParsedPriority
  state: string
  state_category: StateCategory
  state_order: number
  assigned_to: AssignedTo | null
  sprint: SprintInfo | null
  parent_id?: number | null
  in_r1?: boolean
  /** Newly-Added tab: category labels derived from tags. */
  categories?: string[]
  /** Newly-Added tab: open + (tagged Re-Surfaced or ever Done). */
  re_surfaced?: boolean
  ever_done?: boolean
  bundle: BundleInfo | null
  effort: number | null
  tags: string[]
  closed_date: string | null
  created_date: string | null
  changed_date: string | null
  days_in_current_state: number | null
  is_carry_over: boolean
  carry_over_from: string | null
}

export interface ByStateCounts {
  new: number
  grooming: number
  ux_ready: number
  ready: number
  dev_ready: number
  committed: number
  dev_wip: number
  dev_pr: number
  dev_done: number
  qa_ready: number
  qa_wip: number
  done: number
}

export type StateKey = keyof ByStateCounts

export interface PriorityRowCounts {
  is_subtotal: boolean
  color: string
  done_in_previous_sprint: number
  backlog_plus_future: number
  current_sprint_size: number
  by_state: ByStateCounts
  remaining_in_sprint: number
  new_addition: number
  remaining_overall: number
}

export interface Totals {
  p0_open: number
  p1_open: number
  p2_open: number
  p3_open: number
  total_open: number
  current_sprint_size: number
  current_sprint_done: number
  current_sprint_remaining: number
  backlog_plus_future_size: number
}

export interface BySprintEntry {
  by_priority: Record<string, PriorityRowCounts>
}

export interface ByBundleEntry {
  title: string
  code: string
  state: string
  state_counts: Record<string, number>
  item_ids: number[]
}

export interface ByAssigneeEntry {
  p1_open: number
  p2_open: number
  p3_open: number
  by_state: Record<string, number>
}

export interface TrendSeries {
  per_state: { date: string; by_state: Record<string, number> }[]
  open_total: { date: string; count: number }[]
  flow: { date: string; inflow: number; outflow: number }[]
}

export interface Trend extends TrendSeries {
  days: string[]
  /** Same series split per work-item type, so the trend can be type-filtered. */
  by_type?: Record<string, TrendSeries>
}

export interface StandupTask {
  id: number
  url: string
  title: string
  work_item_type: string
  state: string
  state_category: StateCategory
  assigned_to: AssignedTo | null
  effort: number | null
}

export interface StandupItem extends StandupTask {
  priority: ParsedPriority
  tasks: StandupTask[]
}

export interface StandupBundle {
  id: number
  url: string
  title: string
  code: string | null
  state: string
  assigned_to: AssignedTo | null
}

export interface StandupGroup {
  kind: "bundle" | "item"
  owner: AssignedTo | null
  bundle?: StandupBundle
  tasks: StandupTask[]
  items?: StandupItem[]
  item?: StandupItem
}

export interface Standup {
  groups: StandupGroup[]
}

export interface WeeklyDefectsWeek {
  /** ISO week label, e.g. "W28". */
  label: string
  iso_year: number
  iso_week: number
  start_date: string
  end_date: string
  /** The UTC instant the counts were evaluated at (Sun 23:59:59 Toronto,
   *  or the refresh time for the in-progress current week). */
  as_of: string
  /** True for the in-progress week — its counts are "to date", not final. */
  is_current?: boolean
  /** True for the single comparison-baseline week (the regression-1 peak)
   *  that "vs baseline" deltas are measured against. */
  is_baseline?: boolean
}

export type WeeklyDefectsRowKey = "p1_bugs" | "p2_bugs" | "p1_pbis"

export interface WeeklyDefects {
  /** Every completed week across the pinned window (from the regression-1
   *  week) plus the current week, oldest first. */
  weeks: WeeklyDefectsWeek[]
  /** Index into weeks[] of the comparison baseline (regression-1 peak). All
   *  "vs baseline" deltas anchor here, not to weeks[0]. */
  baseline_index?: number
  /** Convenience: weeks[baseline_index].label, e.g. "W21". */
  baseline_label?: string
  /** Open counts at each week's end, aligned index-for-index with weeks. */
  rows: Record<WeeklyDefectsRowKey, number[]>
  /** Gross weekly flows (aligned with weeks): added = entered the row's open
   *  set that week; fixed = left it. Items created AND completed inside one
   *  week count in both. count = prev + added − fixed, exactly. */
  flows?: Record<WeeklyDefectsRowKey, { added: number[]; fixed: number[] }>
  /** Filters/states/types/priority mapping + the WIQL used (reproducibility). */
  methodology: {
    project: string
    work_item_types: string[]
    excluded_states: string[]
    week_definition: string
    history_source: string
    priority_source: string
    rows: Record<WeeklyDefectsRowKey, string>
    /** Prose definition of the added/fixed flow semantics. */
    flows?: string
    wiql: string
  }
}

export interface ReleaseItem {
  id: number
  url: string
  title: string
  work_item_type: string
  state: string
  state_category: StateCategory
  state_order: number
  assigned_to: AssignedTo | null
  priority: { raw: string; major: PriorityMajor; minor: PriorityMinor } | null
  effort: number | null
  tags: string[]
}

export interface Releases {
  /** Discovered version strings (e.g. "1.10.0"), newest first. */
  versions: string[]
  /** Every item carrying a Release-<v> (RC) or GA-<v> (GA) tag. */
  items: ReleaseItem[]
}

export interface Aggregations {
  priority_state_matrix: Record<string, PriorityRowCounts>
  /** Same shape, Bug work items only. Optional: older snapshots may omit it. */
  priority_state_matrix_bugs?: Record<string, PriorityRowCounts>
  /** R1 saved-query items with bundle status resolved. Powers the "Worked On" tab. */
  worked_on?: WorkItem[]
  /** In-scope P1/P2 items created in the last 30 days, grouped by category tag
   *  (untagged → triage bucket). Powers the "Newly Added" tab. */
  newly_added?: WorkItem[]
  /** This week's reconstructed status trend. Powers the "Status Trend" tab. */
  trend?: Trend
  /** Current-sprint work grouped for standup. Powers the "Standup" tab. */
  standup?: Standup
  /** Last 10 completed weeks' open P1/P2 counts (history-accurate via WIQL
   *  ASOF). Powers the "Weekly Trend" tab. Null when history queries failed. */
  weekly_defects?: WeeklyDefects | null
  /** Work items tagged for a release (RC = Release-<v>, GA = GA-<v>). Powers
   *  the "Releases" tab. */
  releases?: Releases | null
  by_sprint: Record<string, BySprintEntry>
  by_bundle: Record<string, ByBundleEntry>
  by_assignee: Record<string, ByAssigneeEntry>
  totals: Totals
}

export interface ReportMetadata {
  wiql_query_ms: number
  items_scanned: number
  items_included: number
  items_excluded_by_scope: number
  source: string
  run_id: string
  future_sprint_window: number
}

export interface SprintReport {
  schema_version: SchemaVersion
  generated_at: string
  sprints: {
    current: Sprint
    previous: Sprint
    next: Sprint
    future: Sprint[]
    all_iterations: string[]
  }
  items: WorkItem[]
  aggregations: Aggregations
  metadata: ReportMetadata
}

/** The order priority rows appear in the matrix view. */
export const PRIORITY_ROW_ORDER: ReadonlyArray<{ key: string; label: string; isSubtotal?: boolean }> = [
  { key: "P0", label: "P0" },
  { key: "P1", label: "P1" },
  { key: "P1-A", label: "P1 A" },
  { key: "P1-B", label: "P1 B" },
  { key: "P1__subtotal", label: "P1 — Subtotal", isSubtotal: true },
  { key: "P2", label: "P2" },
  { key: "P2-A", label: "P2 A" },
  { key: "P2-B", label: "P2 B" },
  { key: "P2__subtotal", label: "P2 — Subtotal", isSubtotal: true },
  { key: "P3", label: "P3" },
]

export const STATE_COLUMN_ORDER: ReadonlyArray<{ key: StateKey; label: string }> = [
  { key: "new", label: "New" },
  { key: "grooming", label: "Grooming" },
  { key: "ux_ready", label: "UX Ready" },
  { key: "ready", label: "Ready" },
  { key: "dev_ready", label: "Dev Ready" },
  { key: "committed", label: "Committed" },
  { key: "dev_wip", label: "Dev WIP" },
  { key: "dev_pr", label: "Dev PR" },
  { key: "dev_done", label: "DEV Done" },
  { key: "qa_ready", label: "QA Ready" },
  { key: "qa_wip", label: "QA WIP" },
  { key: "done", label: "Done" },
]
